import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import SiteHeader from "@/components/pivotbox/SiteHeader";
import SiteFooter from "@/components/pivotbox/SiteFooter";
import FounderSignalTestSection from "@/components/pivotbox/FounderSignalTestSection";

/**
 * /foundersignaltest — Founder Signal Assessment landing page.
 *
 * Renders a Welcome screen first; clicking "Start the Assessment" reveals
 * the existing 20-question assessment card (the same FounderSignalTestSection
 * component that previously lived on the homepage).
 *
 * The submit → /s0-report/:id flow + MongoDB + Google Sheets + PDF + email
 * pipeline are all unchanged — only the URL location of the assessment moved.
 *
 * Visual language: cream background, Playfair Display headings, DM Sans body,
 * green (#2F7D57) accents — matching the rest of the Pivotbox site.
 */
export default function FounderSignalTestPage() {
  const location = useLocation();
  const [started, setStarted] = useState(false);

  // If user lands with ?start=1 (e.g. from a Meta/Google ad) skip the welcome.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("start") === "1") setStarted(true);
  }, [location.search]);

  // Initialise the global "data-reveal" IntersectionObserver so the embedded
  // FounderSignalTestSection (which uses data-reveal) animates in correctly.
  useEffect(() => {
    const elements = Array.from(document.querySelectorAll('[data-reveal="true"]'));
    if (elements.length === 0) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add("is-visible");
        });
      },
      { threshold: 0.16 }
    );
    elements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [started]);

  // When the user clicks Start, smooth-scroll the assessment into view.
  useEffect(() => {
    if (!started) return;
    const el = document.getElementById("founder-signal-test");
    if (el) {
      window.setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 80);
    }
  }, [started]);

  return (
    <div className="pivotbox-shell" data-testid="founder-signal-test-page">
      <SiteHeader testIdPrefix="fst" />

      {!started ? (
        <main data-testid="fst-welcome-main">
          <section className="section-shell fst-welcome-section" data-testid="fst-welcome-section">
            <div className="pivotbox-container">
              <div className="fst-welcome-card" data-testid="fst-welcome-card">
                <span className="fst-welcome-chip" data-testid="fst-welcome-chip">
                  <span className="fst-welcome-chip__dot" aria-hidden="true" />
                  FOUNDER SIGNAL ASSESSMENT
                </span>

                <h1 className="fst-welcome-title" data-testid="fst-welcome-title">
                  Welcome.
                </h1>
                <p className="fst-welcome-subtitle" data-testid="fst-welcome-subtitle">
                  Let&rsquo;s find out what is driving you.
                </p>

                <div className="fst-welcome-divider" aria-hidden="true" />

                <p className="fst-welcome-body__lead" data-testid="fst-welcome-body-lead">
                  Most people who consider starting a business spend months evaluating the idea. Very few take the time to understand what is actually driving them towards it. The business idea comes second. What comes first is always the drive behind it.
                </p>

                <div className="fst-welcome-stats" data-testid="fst-welcome-stats">
                  <div className="fst-welcome-stat">
                    <span className="fst-welcome-stat__value">15</span>
                    <span className="fst-welcome-stat__label">Statements</span>
                  </div>
                  <span className="fst-welcome-stat__sep" aria-hidden="true" />
                  <div className="fst-welcome-stat">
                    <span className="fst-welcome-stat__value">5</span>
                    <span className="fst-welcome-stat__label">Minutes</span>
                  </div>
                  <span className="fst-welcome-stat__sep" aria-hidden="true" />
                  <div className="fst-welcome-stat">
                    <span className="fst-welcome-stat__value">Free</span>
                    <span className="fst-welcome-stat__label">Always</span>
                  </div>
                </div>

                <button
                  type="button"
                  className="fst-welcome-cta"
                  onClick={() => setStarted(true)}
                  data-testid="fst-welcome-start-button"
                >
                  Start the Assessment
                  <ArrowRight size={18} aria-hidden="true" />
                </button>

                <p className="fst-welcome-trust" data-testid="fst-welcome-trust">
                  No sign-up required &nbsp;&middot;&nbsp; Your responses are private &nbsp;&middot;&nbsp; Instant report
                </p>

                <div className="fst-welcome-body" data-testid="fst-welcome-body">
                  <p>
                    This assessment identifies your drive. Without understanding it, you are building on ground you have never examined.
                  </p>
                  <p>
                    This assessment asks you to rate 15 honest statements. There are no right or wrong answers. It is a mirror, not a verdict. Get your 5 page report instantly.
                  </p>
                </div>
              </div>
            </div>
          </section>
        </main>
      ) : (
        <main data-testid="fst-assessment-main">
          <FounderSignalTestSection compact />
        </main>
      )}

      <SiteFooter />
    </div>
  );
}
